'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  href: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage' },
    { label: 'About', href: '/about' },
    { label: 'Services', href: '/services' },
    { label: 'Fleet Evaluation', href: '/fleet-evaluation-process' },
    { label: 'Why Direct Sale', href: '/why-choose-direct-sale' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-card shadow-brand">
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center">
            <svg
              width="280"
              height="40"
              viewBox="0 0 280 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-transform duration-300 hover:scale-105 w-auto h-10"
            >
              <rect width="40" height="40" rx="8" fill="#2C5530" />
              <path
                d="M12 20L18 14L24 20L18 26L12 20Z"
                fill="#D4A574"
              />
              <path
                d="M18 14L24 20L28 16L22 10L18 14Z"
                fill="#4A7C59"
              />
              <text
                x="48"
                y="26"
                fontFamily="Inter Display, system-ui, sans-serif"
                fontSize="18"
                fontWeight="700"
                fill="#2C5530"
              >
                National Fleet Surplus
              </text>
            </svg>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="px-4 py-2 text-sm font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200 ease-out"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link
              href="/contact"
              className="px-6 py-2 text-sm font-semibold text-cta-foreground bg-cta rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand"
            >
              Get Fleet Valued
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors duration-200"
            aria-label="Toggle mobile menu"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border">
            <nav className="flex flex-col px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="px-4 py-3 text-base font-medium text-foreground hover:text-primary hover:bg-muted rounded-md transition-all duration-200 ease-out"
                >
                  {item.label}
                </Link>
              ))}
              <Link
                href="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="px-4 py-3 text-base font-semibold text-center text-cta-foreground bg-cta rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out active:scale-95"
              >
                Get Fleet Valued
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;